'use client';

import React, { useState } from 'react';
import { MoreHorizontal, Phone, MapPin } from 'lucide-react';
import Badge from '@/components/ui/Badge';

type OrderStatus = 'new' | 'confirmed' | 'preparing' | 'ready' | 'out-for-delivery' | 'delivered' | 'cancelled';

interface Order {
  id: string;
  customer: string;
  phone: string;
  products: string;
  containers: string;
  amount: string;
  area: string;
  driver: string;
  deliveryTime: string;
  status: OrderStatus;
}

const orders: Order[] = [
  { id: 'ord-001', customer: 'Natalie Joseph', phone: '+1 268 764 3201', products: 'Original × 2, Spicy × 1', containers: '2M + 1S', amount: '$38.00', area: 'St. John\'s North', driver: 'Derrick M.', deliveryTime: '09:00–11:00', status: 'out-for-delivery' },
  { id: 'ord-002', customer: 'Ricardo Browne', phone: '+1 268 720 8845', products: 'Family Pack × 1', containers: '1 Family', amount: '$45.00', area: 'Bolans Village', driver: 'Keisha T.', deliveryTime: '10:00–12:00', status: 'preparing' },
  { id: 'ord-003', customer: 'Sandra Charles', phone: '+1 268 770 5590', products: 'Roasted Garlic × 3', containers: '3M', amount: '$51.00', area: 'English Harbour', driver: 'Unassigned', deliveryTime: '13:00–15:00', status: 'confirmed' },
  { id: 'ord-004', customer: 'Leroy Francis', phone: '+1 268 788 2211', products: 'Original × 1, Large × 1', containers: '1S + 1L', amount: '$52.00', area: 'Parham', driver: 'Marcus R.', deliveryTime: '14:00–16:00', status: 'ready' },
  { id: 'ord-005', customer: 'Yvette Thomas', phone: '+1 268 765 0934', products: 'Spicy × 2', containers: '2L', amount: '$64.00', area: 'Friars Hill', driver: 'Unassigned', deliveryTime: '15:00–17:00', status: 'new' },
  { id: 'ord-006', customer: 'Calvin Peters', phone: '+1 268 723 4412', products: 'Original × 4', containers: '4S', amount: '$44.00', area: 'St. John\'s South', driver: 'Derrick M.', deliveryTime: '09:00–11:00', status: 'delivered' },
  { id: 'ord-007', customer: 'Monique Henry', phone: '+1 268 799 1108', products: 'Family Pack × 2', containers: '2 Family', amount: '$90.00', area: 'All Saints', driver: 'Keisha T.', deliveryTime: '11:00–13:00', status: 'out-for-delivery' },
  { id: 'ord-008', customer: 'Trevor Simon', phone: '+1 268 712 6630', products: 'Roasted Garlic × 1', containers: '1M', amount: '$17.00', area: 'Jolly Harbour', driver: 'Unassigned', deliveryTime: '16:00–18:00', status: 'cancelled' },
];

const statusLabels: Record<OrderStatus, string> = {
  new: 'New',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  'out-for-delivery': 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const filterTabs = [
  { id: 'filter-all', label: 'All', value: 'all' },
  { id: 'filter-new', label: 'New', value: 'new' },
  { id: 'filter-preparing', label: 'Preparing', value: 'preparing' },
  { id: 'filter-out', label: 'On Route', value: 'out-for-delivery' },
  { id: 'filter-delivered', label: 'Delivered', value: 'delivered' },
];

export default function OrderPipeline() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const filtered = activeFilter === 'all' ? orders : orders.filter(o => o.status === activeFilter);

  return (
    <div className="card-elevated flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
        <div>
          <h2 className="text-base font-700 text-foreground">Today&apos;s Order Pipeline</h2>
          <p className="text-xs text-muted-foreground">{orders.length} orders · Wednesday 30 Jul 2026</p>
        </div>
        <button className="text-xs font-600 text-primary hover:underline">View all orders →</button>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 px-5 py-3 overflow-x-auto scrollbar-thin">
        {filterTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveFilter(tab.value)}
            className={`px-3 py-1.5 rounded-md text-xs font-600 whitespace-nowrap transition-all duration-150 ${
              activeFilter === tab.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            {tab.label}
            <span className="ml-1.5 font-tabular">
              {tab.value === 'all' ? orders.length : orders.filter(o => o.status === tab.value).length}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {['Customer', 'Products', 'Area', 'Delivery Window', 'Driver', 'Amount', 'Status', ''].map((col, i) => (
                <th
                  key={`col-${i}`}
                  className="px-4 py-2.5 text-left text-xs font-600 uppercase tracking-wider text-muted-foreground whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(order => (
              <tr
                key={order.id}
                onMouseEnter={() => setHoveredRow(order.id)}
                onMouseLeave={() => setHoveredRow(null)}
                className={`border-b border-border/50 transition-colors duration-100 ${
                  hoveredRow === order.id ? 'bg-muted/50' : ''
                }`}
              >
                <td className="px-4 py-3">
                  <p className="font-600 text-foreground text-sm leading-tight">{order.customer}</p>
                  <p className="text-xs text-muted-foreground">{order.phone}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm text-foreground leading-tight truncate max-w-[140px]">{order.products}</p>
                  <p className="text-xs text-muted-foreground">{order.containers}</p>
                </td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
                    <MapPin size={11} className="flex-shrink-0" />
                    {order.area}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground font-tabular">{order.deliveryTime}</td>
                <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">
                  {order.driver === 'Unassigned'
                    ? <span className="text-warning text-xs font-600">⚠ Unassigned</span>
                    : order.driver
                  }
                </td>
                <td className="px-4 py-3 font-tabular font-700 text-foreground whitespace-nowrap">{order.amount}</td>
                <td className="px-4 py-3">
                  <Badge variant={order.status} label={statusLabels[order.status]} />
                </td>
                <td className="px-4 py-3">
                  {hoveredRow === order.id && (
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Call customer">
                        <Phone size={14} />
                      </button>
                      <button className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="More actions">
                        <MoreHorizontal size={14} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="py-10 text-center">
            <p className="text-muted-foreground text-sm">No orders in this status</p>
          </div>
        )}
      </div>
    </div>
  );
}